import { Grid } from "@material-ui/core";
import {
  Send,
  X,
  Smile,
  File as FileIcon,
  Loader,
  Mic,
  Square,
  Plus,
  Paperclip,
} from "react-feather";
import styled from "styled-components/macro";
import TextareaAutosize from "react-textarea-autosize";
import { useRef, useState, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";

const ChatInputsTwo = styled(Grid)`
  min-height: 40px;
  padding: 16px;
  position: relative;
`;

const CompactChatInputSec = styled.div`
  background: #ffffff !important;
  border: 1px solid #e1e1e1 !important;
  border-radius: 8px !important;
  width: 100% !important;
  display: flex !important;
  align-items: flex-end !important;
  padding: 8px !important;
  gap: 6px !important;

  textarea {
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 22px !important;
    color: #343434 !important;
    padding: 9px 10px 7px 10px !important;
    width: 100% !important;
    border: none !important;
    display: flex !important;
    align-items: center !important;
    resize: none !important;
    height: 39px !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  textarea::-webkit-scrollbar {
    width: 5px !important;
  }
  textarea::-webkit-scrollbar-track {
    background: transparent !important;
  }
  textarea::-webkit-scrollbar-thumb {
    background: #ced0d1 !important;
  }
  textarea::-webkit-scrollbar-thumb:hover {
    background: #ced0d1 !important;
  }
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  bottom: 52px;
  left: 40px;
  z-index: 10;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;

  /* emoji-picker-react sets its own defaults directly on .EmojiPickerReact,
     so overriding these variables on a parent div is ignored — they have
     to be redeclared on that same class, with !important, to actually win. */
  .EmojiPickerReact {
    --epr-emoji-size: 20px !important;
    --epr-header-padding: 8px !important;
    --epr-search-input-height: 32px !important;
    --epr-category-navigation-button-size: 22px !important;
    --epr-category-label-height: 24px !important;
    --epr-preview-height: 0px !important;
    --epr-emoji-gap: 2px !important;
    --epr-search-input-text-size: 12px !important;
    --epr-category-label-text-size: 11px !important;
  }

  /* Fall back to the underlying class names directly — some versions of
     emoji-picker-react don't wire every text size to a CSS variable. */
  .epr-search-container input {
    font-size: 13px !important;
  }
  .epr-emoji-category-label {
    font-size: 13px !important;
    font-weight: 500 !important;
    padding: 4px 8px !important;
  }
`;

const ActionButton = styled.button`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 50% !important;
  display: flex !important;
  color: #ff8271 !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  cursor: pointer !important;
  transition: background-color 0.2s ease-in-out !important;

  &:hover {
    background-color: rgba(255, 130, 113, 0.1) !important;
  }

  svg {
    width: 18px !important;
    height: 18px !important;
  }
`;

const SendButton = styled.button`
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  background: #ff8271 !important;
  border: none !important;
  border-radius: 50% !important;
  display: flex !important;
  color: #fff !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease-in-out !important;
  cursor: pointer !important;

  &:hover {
    background: #e97565 !important;
  }

  svg {
    width: 16px !important;
    height: 16px !important;
    margin-left: 2px !important;
  }
`;

const MAX_RECORDING_SECONDS = 300; // 5 minutes

const IdentityChatInputBox = ({
  messageTextVal,
  handleChange,
  sendMessage,
  pendingAttachment,
  onSelectFile,
  onSelectRecordedAudio,
  onCancelAttachment,
  isUploading,
}) => {
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isEmojiOpen &&
        emojiPopoverRef.current &&
        !emojiPopoverRef.current.contains(e.target) &&
        !emojiButtonRef.current?.contains(e.target)
      ) {
        setIsEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEmojiOpen]);

  // Clean up mic stream and timer if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleEmojiClick = (emojiData) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      handleChange({ target: { value: messageTextVal + emojiData.emoji } });
      return;
    }
    const start = textarea.selectionStart ?? messageTextVal.length;
    const end = textarea.selectionEnd ?? messageTextVal.length;
    const newValue =
      messageTextVal.slice(0, start) +
      emojiData.emoji +
      messageTextVal.slice(end);
    handleChange({ target: { value: newValue } });
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + emojiData.emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onSelectRecordedAudio(
        null,
        "Voice recording isn't supported in this browser."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recordedChunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const file = new File(
          [blob],
          `voice-note-${Date.now()}.${
            mimeType.includes("webm") ? "webm" : "m4a"
          }`,
          {
            type: mimeType,
          }
        );
        onSelectRecordedAudio(file, null);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev + 1;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.log("getUserMedia failed", err);
      onSelectRecordedAudio(
        null,
        "Microphone access was denied. Please allow microphone access to record a voice note."
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const formatSeconds = (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div>
      {pendingAttachment && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 16px 8px",
          }}
        >
          {pendingAttachment.type.startsWith("image/") ? (
            <img
              src={pendingAttachment.previewUrl}
              alt=""
              style={{
                width: "48px",
                height: "48px",
                objectFit: "cover",
                borderRadius: "6px",
                marginRight: "8px",
              }}
            />
          ) : pendingAttachment.type.startsWith("audio/") ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
            >
              <Mic size={18} style={{ marginRight: "6px", color: "#502688" }} />
              <span style={{ fontSize: "13px" }}>Voice note</span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
            >
              <FileIcon
                size={20}
                style={{ marginRight: "6px", color: "#502688" }}
              />
              <span style={{ fontSize: "13px" }}>{pendingAttachment.name}</span>
            </div>
          )}
          <button
            onClick={onCancelAttachment}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <ChatInputsTwo>
        {isEmojiOpen && (
          <EmojiPickerWrapper ref={emojiPopoverRef}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              height={320}
              width={350}
              previewConfig={{ showPreview: false }}
              searchDisabled={false}
            />
          </EmojiPickerWrapper>
        )}

        {isRecording ? (
          <CompactChatInputSec style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#e23725",
                }}
              />
              <span style={{ fontSize: "14px", color: "#343434" }}>
                Recording… {formatSeconds(recordingSeconds)}
              </span>
            </div>
            <SendButton
              onClick={stopRecording}
              style={{ background: "#e23725", borderColor: "#e23725" }}
              type="button"
            >
              <Square size={18} />
            </SendButton>
          </CompactChatInputSec>
        ) : (
          <CompactChatInputSec>
            <ActionButton
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Paperclip />
            </ActionButton>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.[0]) onSelectFile(e.target.files[0]);
                e.target.value = "";
              }}
            />

            <ActionButton
              ref={emojiButtonRef}
              onClick={() => setIsEmojiOpen((prev) => !prev)}
              type="button"
            >
              <Smile />
            </ActionButton>

            <Grid item style={{ flexGrow: 1 }}>
              <TextareaAutosize
                ref={textareaRef}
                minRows={1}
                maxRows={3}
                placeholder="Type your message"
                value={messageTextVal}
                onChange={handleChange}
              />
            </Grid>

            {!(messageTextVal || "").trim() && !pendingAttachment ? (
              <ActionButton onClick={startRecording} type="button">
                <Mic />
              </ActionButton>
            ) : (
              <SendButton
                onClick={sendMessage}
                disabled={isUploading}
                style={{ opacity: isUploading ? 0.6 : 1 }}
                type="button"
              >
                {isUploading ? <Loader size={18} className="spin" /> : <Send />}
              </SendButton>
            )}
          </CompactChatInputSec>
        )}
      </ChatInputsTwo>
    </div>
  );
};

export default IdentityChatInputBox;